'use client'

import type { MouseEvent } from 'react'
import type {
	ButtonProps,
	SRT_Cell,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import { useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { parseFromValuesOrFunc } from '../../utils/utils'
import { SRT_RowActionMenu } from '../menus/SRT_RowActionMenu'
import { SRT_EditActionButtons } from './SRT_EditActionButtons'

export type SRT_ToggleRowActionMenuButtonProps<TData extends SRT_RowData> = {
	cell: SRT_Cell<TData>
	row: SRT_Row<TData>
	staticRowIndex?: number
	table: SRT_TableInstance<TData>
} & ButtonProps

export const SRT_ToggleRowActionMenuButton = <TData extends SRT_RowData>({
	cell,
	row,
	staticRowIndex,
	table,
	className,
	...rest
}: SRT_ToggleRowActionMenuButtonProps<TData>) => {
	const {
		getState,
		options: {
			createDisplayMode,
			editDisplayMode,
			enableEditing,
			icons: { EditIcon, MoreVertIcon },
			localization,
			renderRowActionMenuItems,
			renderRowActions,
		},
		setEditingRow,
	} = table

	const { creatingRow, editingRow } = getState()

	const isCreating = creatingRow?.id === row.id
	const isEditing = editingRow?.id === row.id

	const showEditActionButtons =
		(isCreating && createDisplayMode === 'row') ||
		(isEditing && editDisplayMode === 'row')

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

	const handleOpenRowActionMenu = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation()
		event.preventDefault()
		setAnchorEl(event.currentTarget)
	}

	const handleStartEditMode = (event: MouseEvent) => {
		event.stopPropagation()
		setEditingRow({ ...row })
		setAnchorEl(null)
	}

	// Tailwind-inspired opacity and hover styles to mimic the original MUI behavior
	const commonButtonClasses =
		'opacity-50 hover:opacity-100 transition-opacity duration-150'

	return renderRowActions && !showEditActionButtons ? (
		renderRowActions({ cell, row, staticRowIndex, table })
	) : showEditActionButtons ? (
		<SRT_EditActionButtons row={row} table={table} />
	) : !renderRowActionMenuItems &&
	  parseFromValuesOrFunc(enableEditing, row) &&
	  ['modal', 'row'].includes(editDisplayMode ?? '') ? (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant='ghost'
					size='icon-sm'
					aria-label={localization.edit}
					onClick={handleStartEditMode}
					className={`${commonButtonClasses} ${className}`}
					{...rest}
				>
					<EditIcon />
				</Button>
			</TooltipTrigger>
			<TooltipContent>{localization.edit}</TooltipContent>
		</Tooltip>
	) : renderRowActionMenuItems?.({
			row,
			staticRowIndex,
			table,
	  } as any)?.length ? (
		<>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						variant='ghost'
						size='icon-sm'
						aria-label={localization.rowActions}
						onClick={handleOpenRowActionMenu}
						className={`${commonButtonClasses} ${className}`}
						{...rest}
					>
						<MoreVertIcon />
					</Button>
				</TooltipTrigger>
				<TooltipContent>{localization.rowActions}</TooltipContent>
			</Tooltip>
			<SRT_RowActionMenu
				anchorEl={anchorEl}
				handleEdit={handleStartEditMode}
				row={row}
				setAnchorEl={setAnchorEl}
				staticRowIndex={staticRowIndex}
				table={table}
			/>
		</>
	) : null
}
